import get from 'lodash/get';
import React, { useEffect, useState, useMemo } from 'react';
import * as PropTypes from 'prop-types';
import { GoogleMap, Marker, InfoWindow } from 'react-google-maps';

import {
  computeDistanceBetweenLocations,
  currentWorkingHours,
  mapStylesCustom,
} from './utils';
import locationPNG from '../../assets/icons/png/blue.png';
import activeLocationPNG from '../../assets/icons/png/black.png';
import myPosition from '../../assets/icons/png/my_location_icon.png';
import { LocationDetailsView } from './LocationDetailsPopUp';
import { useGoogleMapsData } from './useGoogleMapsData';
import enhance from './enhance';
import styles from './styles.styl';

function _GoogleMaps(props) {
  const {
    isUpcoming,
    appointmentsList,
    currentAppointment,
    historyAppointmentsList,
    currentAppointmentId,
    getLocationsDetails,
    activeTodoItemTaskId,
    geolocations,
    onBlur,
    onFocus,
    intl: { formatMessage },
    isAppointment,
    isPaperworkFlow = false,
    paperworkFlowAppointment,
  } = props;

  const activeAppointment = useMemo(() => {
    if (currentAppointment) {
      return currentAppointment;
    }
    if (isUpcoming) {
      return appointmentsList.find(
        appointment => appointment.id === currentAppointmentId,
      );
    }

    if (isPaperworkFlow) {
      return paperworkFlowAppointment;
    }

    return historyAppointmentsList.find(
      appointment => appointment.id === currentAppointmentId,
    );
  }, [currentAppointmentId, appointmentsList, currentAppointment]);

  const appointmentLocation = get(activeAppointment, 'location', {});
  const startTime = get(activeAppointment, 'dateTime', '');

  const {
    mapRef,
    currentPosition,
    zoom,
    isMyPosition,
    handleMapDragEnd,
    myGeoLocation,
  } = useGoogleMapsData();

  useEffect(() => {
    if (!isAppointment) {
      getLocationsDetails({ id: activeTodoItemTaskId });
    }
  }, []);

  const [showInfoWindowId, setShowInfoWindowId] = useState(null);
  const [activeLocation, setActiveLocation] = useState(null);

  const viewMarker = useMemo(
    () => marker => {
      const { id, lat, lng, schedule, name, geofenceLat, geofenceLng } = marker;
      const delta = isMyPosition
        ? `${computeDistanceBetweenLocations(myGeoLocation, {
            lat: lat || geofenceLat,
            lng: lng || geofenceLng,
          })}${formatMessage({ id: 'googleMaps.miAway' })}`
        : '';
      const workingHours = currentWorkingHours(
        formatMessage,
        schedule,
        true,
        false,
      );
      return (
        <Marker
          onFocus={onFocus}
          onBlur={onBlur}
          onMouseOver={() => {
            setShowInfoWindowId(id);
          }}
          onMouseOut={() => {
            setShowInfoWindowId(null);
          }}
          icon={
            activeLocation && activeLocation.id === id
              ? activeLocationPNG
              : locationPNG
          }
          onClick={() => setActiveLocation(marker)}
          position={{ lat: lat || geofenceLat, lng: lng || geofenceLng }}
          key={id}
        >
          {showInfoWindowId === id && (
            <InfoWindow>
              <div className={styles.wrapper}>
                <div className={styles.titleLocation}>{name}</div>
                <div className={styles.description}>
                  {delta}
                  {!isAppointment && workingHours}
                </div>
              </div>
            </InfoWindow>
          )}
        </Marker>
      );
    },
    [myGeoLocation, activeLocation, showInfoWindowId],
  );

  return (
    <div>
      <GoogleMap
        zoom={zoom}
        center={
          isAppointment
            ? {
                lat: appointmentLocation.geofenceLat,
                lng: appointmentLocation.geofenceLng,
              }
            : currentPosition
        }
        ref={mapRef}
        options={{
          streetViewControl: false,
          scrollwheel: true,
          fullscreenControl: false,
          disableDefaultUI: true,
          scaleControl: true,
          zoomControl: true,
          styles: mapStylesCustom,
        }}
        onDragEnd={handleMapDragEnd}
      >
        {!isAppointment && geolocations.map(marker => viewMarker(marker))}
        {isAppointment && viewMarker(appointmentLocation)}
        {isMyPosition && <Marker position={myGeoLocation} icon={myPosition} />}
      </GoogleMap>
      <LocationDetailsView
        startTime={startTime}
        isAppointment={isAppointment}
        formatMessage={formatMessage}
        myGeoLocation={myGeoLocation}
        location={activeLocation}
        opened={!!activeLocation}
        isMyPosition={isMyPosition}
        onClose={() => setActiveLocation(null)}
      />
    </div>
  );
}

_GoogleMaps.propTypes = {
  isUpcoming: PropTypes.bool,
  getLocationsDetails: PropTypes.func,
  activeTodoItemTaskId: PropTypes.number,
  geolocations: PropTypes.array,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  isAppointment: PropTypes.bool,
  isPaperworkFlow: PropTypes.bool,
  paperworkFlowAppointment: PropTypes.object,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }),
};

_GoogleMaps.defaultProps = {
  isAppointment: false,
  onFocus: () => {},
  onBlur: () => {},
};

const GoogleMaps = enhance(_GoogleMaps);

export default GoogleMaps;



// WEBPACK FOOTER //
// ./src/components/GoogleMaps/index.jsx